import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { Spinner } from '@/components/ui'

import { CONTENT_WRAPPER_ID } from '../../constants'

type HTMLViewerProps = {
  content?: string
  isLoading: boolean
  error?: { message?: string } | null
  shadowHostRef: React.RefObject<HTMLDivElement>
}

/**
 * HTMLViewer Component
 *
 * Renders HTML document content using Shadow DOM for style isolation.
 * This component creates a shadow root and injects the HTML content
 * with appropriate styling.
 *
 * Note: Currently supports HTML content only. PDF support will be
 * implemented in a future component (e.g., DocumentPDFViewer).
 *
 * @param htmlContent - The decoded HTML string to render
 * @param isLoading - Whether the content is currently being loaded
 * @param error - Any error that occurred during loading
 */
const HTMLViewer: React.FC<HTMLViewerProps> = ({
  content,
  isLoading,
  error,
  shadowHostRef,
}) => {
  const { t } = useTranslation()

  // Loading state
  if (isLoading && !content) {
    return (
      <div className="flex items-center justify-center p-8 flex-1">
        <Spinner />
      </div>
    )
  }

  // Error state
  if (error && !content) {
    return (
      <div className="flex items-center justify-center p-8 flex-1">
        <div className="text-center">
          <p className="text-secondary-700 mb-2">
            {t('versionComparison.viewer.failedToLoadContent')}
          </p>
          <p className="text-sm text-tertiary-600">
            {error.message || t('versionComparison.layout.pleaseTryAgainLater')}
          </p>
        </div>
      </div>
    )
  }

  // Empty state
  if (!content) {
    return (
      <div className="flex items-center justify-center p-8 flex-1">
        <div className="text-center">
          <p className="text-secondary-700">
            {t('versionComparison.viewer.noContentAvailable')}
          </p>
        </div>
      </div>
    )
  }

  // Render shadow host
  return (
    <div
      ref={shadowHostRef}
      id={CONTENT_WRAPPER_ID}
      className="block size-full"
      aria-label={t('versionComparison.viewer.htmlViewerAriaLabel')}
    />
  )
}

const HTMLViewerMemoized = React.memo(HTMLViewer)
HTMLViewerMemoized.displayName = 'HTMLViewer'

export { HTMLViewerMemoized as HTMLViewer }
